import type { AxiosPromise } from "axios";
import { isAxiosError } from "axios";

import i18n from "~/i18n";
import { Errors } from "~/utils";

export class ApiError<T> extends Error {
  status: number | null;
  error: unknown;
  errorMessage: string;
  errorParsed: T;

  constructor(
    status: number | null,
    error: unknown,
    errorMessage: string,
    errorParsed: T,
  ) {
    super(errorMessage);
    this.status = status;
    this.error = error;
    this.errorMessage = errorMessage;
    this.errorParsed = errorParsed;
  }
}

type AsyncApiFunctions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: (...args: any[]) => AxiosPromise<any>;
};

export type ApiResult<TData, TError = unknown> =
  | { ok: true; data: TData }
  | {
      ok: false;
      error: ApiError<TError>;
    };

type ErrorParsers<T extends AsyncApiFunctions> = Partial<{
  [K in keyof T]: (
    error: unknown,
    data: Parameters<T[K]>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => { message: string } | any;
}>;

/*
[K in keyof T]: (
    ...args: Parameters<T[K]>
  ) => Promise<ApiResult<Awaited<ReturnType<T[K]>>['data'], U[K]>>;
*/
export function createAPISlice<
  T extends AsyncApiFunctions,
  EP extends ErrorParsers<T>,
>(
  apis: T,
  errorParsers?: EP,
): {
  [K in keyof T]: (
    ...args: Parameters<T[K]>
  ) => Promise<
    EP[K] extends (e: unknown) => infer ParsedError
      ? ApiResult<Awaited<ReturnType<T[K]>>["data"], ParsedError>
      : ApiResult<Awaited<ReturnType<T[K]>>["data"], undefined>
  >;
} {
  const wrapped: Partial<Record<keyof T, unknown>> = {};

  for (const key in apis) {
    wrapped[key] = async (...args: unknown[]) => {
      try {
        const apiFunction = apis[key];
        if (!apiFunction) {
          throw new Error(`API function for key "${key}" not found.`);
        }
        const response = await apiFunction?.(...args);
        return { ok: true, data: response.data };
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorParsed = errorParsers?.[key]?.(error, args as any);
        const errorMessage =
          errorParsed && "message" in errorParsed && errorParsed?.message
            ? errorParsed.message
            : Errors.getErrorMessage(error, i18n.t);
        let status: number | null = null;
        if (isAxiosError(error)) {
          status = error.response?.status || null;
        }
        return {
          ok: false,
          error: new ApiError(status, error, errorMessage, errorParsed),
        };
      }
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return wrapped as any;
}
